/**
 * Pure recommendation logic — no I/O, no maintained price list. Two kinds:
 *  - **footing**: per provider, is flat seat/subscription cost cheaper than metered
 *    API at the projected run rate (or vice versa)? Emitted only when a seat price
 *    is known — never guessed.
 *  - **routing**: is low/medium-difficulty work running on a model whose OBSERVED
 *    $/token (from real allocated spend) is well above a cheaper model already in
 *    the fleet? Rates are empirical — derived from actual usage, so nothing to
 *    maintain by hand.
 * Shared by bmai-web now and ported to bmai-insights later.
 */
import { formatUsd } from '@/shared/analytics/MetricFormatting';

export type RecommendationTone = 'opportunity' | 'watch';

export interface Recommendation {
    id: string;
    kind: 'footing' | 'routing';
    tone: RecommendationTone;
    title: string;
    detail: string;
    /** Estimated monthly $ saved by taking the recommendation. */
    monthlyImpactUsd: number;
}

/** Below this monthly delta a recommendation is noise and is dropped. */
const MIN_MONTHLY_IMPACT = 10;

export interface FootingInput {
    /** Provider display label. */
    provider: string;
    /** Projected monthly metered (API) spend for this provider. */
    meteredMonthly: number;
    /** Estimated monthly flat seat/subscription cost, or null when unknown (then no rec). */
    seatMonthly: number | null;
}

export function footingRecommendations(items: FootingInput[]): Recommendation[] {
    const recs: Recommendation[] = [];
    for (const item of items) {
        if (item.seatMonthly === null || item.seatMonthly <= 0 || item.meteredMonthly <= 0) continue;
        const delta = Math.abs(item.meteredMonthly - item.seatMonthly);
        if (delta < MIN_MONTHLY_IMPACT) continue;
        const meteredCheaper = item.meteredMonthly < item.seatMonthly;
        recs.push({
            id: `footing:${item.provider}`,
            kind: 'footing',
            tone: 'opportunity',
            title: meteredCheaper ? `${item.provider}: stay on metered API` : `${item.provider}: move to seat licenses`,
            detail:
                `Metered ~${formatUsd(item.meteredMonthly)}/mo vs seats ~${formatUsd(item.seatMonthly)}/mo — ` +
                `${meteredCheaper ? 'metered' : 'seats'} saves ~${formatUsd(delta)}/mo at the current run rate.`,
            monthlyImpactUsd: delta,
        });
    }
    return recs.sort((a, b) => b.monthlyImpactUsd - a.monthlyImpactUsd);
}

export interface ModelRoutingInput {
    model: string;
    /** Monthly tokens eligible to move (projected low/medium-difficulty volume). */
    lightMonthlyTokens: number;
    /** Observed $ per 1M tokens for this model (allocated spend ÷ tokens). */
    ratePerMTok: number;
}

/**
 * Suggests moving a pricier model's lighter work to the cheapest model already
 * proven in the fleet. Savings priced off the observed rate gap — no list prices.
 */
export function routingRecommendations(models: ModelRoutingInput[]): Recommendation[] {
    const rated = models.filter((model) => model.ratePerMTok > 0 && model.lightMonthlyTokens > 0);
    if (rated.length < 2) return [];
    const cheapest = rated.reduce((min, model) => (model.ratePerMTok < min.ratePerMTok ? model : min));

    const recs: Recommendation[] = [];
    for (const model of rated) {
        if (model.model === cheapest.model) continue;
        const savings = ((model.ratePerMTok - cheapest.ratePerMTok) * model.lightMonthlyTokens) / 1_000_000;
        if (savings < MIN_MONTHLY_IMPACT) continue;
        recs.push({
            id: `routing:${model.model}`,
            kind: 'routing',
            tone: 'opportunity',
            title: `Route ${model.model}'s lighter work to a cheaper model`,
            detail:
                `${model.model} runs at ~$${model.ratePerMTok.toFixed(2)}/1M tokens vs ${cheapest.model} at ~$${cheapest.ratePerMTok.toFixed(2)}. ` +
                `Moving its low/medium-difficulty volume could save ~${formatUsd(savings)}/mo.`,
            monthlyImpactUsd: savings,
        });
    }
    return recs.sort((a, b) => b.monthlyImpactUsd - a.monthlyImpactUsd);
}
